import { AuthContext } from "@/contexts/AuthContext";
import {
    COMPLETE_TWITCH_AUTH_MUTATION,
    LOGIN_USER_MUTATION,
    LOGOUT_USER_MUTATION,
    START_TWITCH_AUTH_MUTATION,
} from "@/graphql/mutations/authMutations";
import { CHECK_TWITCH_TOKEN_STATUS_QUERY } from "@/graphql/queries/streamQueries";
import { router } from "@/router/browserRouter";
import { UserData } from "@/types/globalTypes";
import { gql, useApolloClient } from "@apollo/client";
import isEqual from "lodash.isequal";
import { useCallback, useEffect, useRef, useState } from "react";

const USER_QUERY = gql`
    query UserQuery {
        isAuthenticated
        user {
            id
            twitchUsername
            email
            avatarUrl
            isStaff
            isSuperuser
            player {
                id
                name
                vip
                twitchId
                tagOfVanderwood
                goldenWohToken
                permanentVip
                nickname
                moderator
                enabled
                isGota
                gotaSubTimeInDays
                overallSubTimeInDays
                avatarUrl
                hasSeenWelcomeMessage
                clan {
                    id
                    name
                    tag
                    twitchEmojiName
                    color {
                        hex
                    }
                    borderColor {
                        hex
                    }
                    rankNumberColor {
                        hex
                    }
                    rankColor {
                        hex
                    }
                }
                shield {
                    id
                    shieldNumber {
                        number
                    }
                    imageUrl
                    imageWithTagsUrl
                    createdAt
                    updatedAt
                }
                shieldTwo {
                    id
                    shieldNumber {
                        number
                    }
                    imageUrl
                    imageWithTagsUrl
                    createdAt
                    updatedAt
                }
                bux {
                    balance
                }
            }
        }
    }
`;

interface AuthProviderProps {
    children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const [user, setUser] = useState<UserData | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [twitchTokenInvalid, setTwitchTokenInvalid] = useState(false);
    const client = useApolloClient();
    const { navigate } = router;

    // Ref to mark when the initial fetch is done so that we only use the loading state once.
    const initialFetchDone = useRef(false);

    const fetchUserData = useCallback(async () => {
        if (!initialFetchDone.current) {
            setLoading(true);
        }

        try {
            const { data } = await client.query({
                query: USER_QUERY,
                fetchPolicy: "network-only",
            });

            if (data.isAuthenticated) {
                if (
                    !isEqual(data.user, user) ||
                    isAuthenticated !== data.isAuthenticated
                ) {
                    setUser(data.user);
                    setIsAuthenticated(true);
                }
            } else {
                // This case is unlikely with isAuthenticated == false but still a 200
                setUser(null);
                setIsAuthenticated(false);
            }
        } catch (error: any) {
            console.error("Error fetching user data:", error);

            const statusCode = error?.networkError?.statusCode;

            const isAuthError =
                statusCode === 401 ||
                statusCode === 403 ||
                error?.graphQLErrors?.some(
                    (e: any) => e.extensions?.code === "UNAUTHENTICATED",
                );

            if (isAuthError) {
                setUser(null);
                setIsAuthenticated(false);
            } else {
                // It's a temporary error (backend down or 5xx), do not unset auth
                console.warn("Temporary fetch error, session not cleared");
            }
        } finally {
            if (!initialFetchDone.current) {
                setLoading(false);
                initialFetchDone.current = true;
            }
        }
    }, [client, user, isAuthenticated]);

    const checkTwitchTokenStatus = useCallback(async () => {
        // Only check if user is authenticated
        if (!isAuthenticated) {
            setTwitchTokenInvalid(false);
            return;
        }

        try {
            const { data } = await client.query({
                query: CHECK_TWITCH_TOKEN_STATUS_QUERY,
                fetchPolicy: "network-only",
            });

            console.debug(
                "Twitch token status check result:",
                data.checkTwitchTokenStatus,
            );
            setTwitchTokenInvalid(data.checkTwitchTokenStatus || false);
        } catch (error) {
            console.error("Error checking Twitch token status:", error);
            // On error, assume token is valid to avoid false positives
            setTwitchTokenInvalid(false);
        }
    }, [client, isAuthenticated]);

    const startTwitchLogin = async () => {
        try {
            const { data } = await client.mutate({
                mutation: START_TWITCH_AUTH_MUTATION,
            });
            if (data.startTwitchAuth.url) {
                window.location.href = data.startTwitchAuth.url;
            }
        } catch (error) {
            console.error("Error starting Twitch login:", error);
        }
    };

    const completeTwitchLogin = async (code: string) => {
        try {
            const { data } = await client.mutate({
                mutation: COMPLETE_TWITCH_AUTH_MUTATION,
                variables: { code },
            });
            if (data.completeTwitchAuth.user) {
                await fetchUserData(); // Refresh user data after login
                navigate("/forge");
            }
        } catch (error) {
            console.error("Error completing Twitch login:", error);
        }
    };

    const loginUser = async (email: string, password: string) => {
        try {
            const { data } = await client.mutate({
                mutation: LOGIN_USER_MUTATION,
                variables: { email, password },
            });
            if (data.loginUser.success) {
                await fetchUserData(); // Refresh user data after login
                navigate("/dashboard"); // Redirect after login success
            } else {
                throw new Error(data.loginUser.message);
            }
        } catch (error) {
            console.error("Error logging in user:", error);
            throw error;
        }
    };

    const logoutUser = async () => {
        try {
            const { data } = await client.mutate({
                mutation: LOGOUT_USER_MUTATION,
            });
            if (data.logoutUser.success) {
                setUser(null);
                setIsAuthenticated(false);
                navigate("/");
            }
        } catch (error) {
            console.error("Logout error:", error);
        }
    };

    // Fetch user data once on mount, then rely on subscriptions for updates
    useEffect(() => {
        // Initial fetch on mount only
        fetchUserData();
    }, [fetchUserData]);

    // Check Twitch token status after authentication state changes
    useEffect(() => {
        if (isAuthenticated && initialFetchDone.current) {
            checkTwitchTokenStatus();
        }
    }, [isAuthenticated, checkTwitchTokenStatus]);

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                isAuthenticated,
                twitchTokenInvalid,
                fetchUserData,
                startTwitchLogin,
                completeTwitchLogin,
                loginUser,
                logoutUser,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
